import type { AccessPolicy, AccessPolicyUpdate } from "./_shared"

import { api } from "@/lib/api-client"

export type AdminTrainingStatusResponse = {
  overallPct: number
  summary?: {
    totalUsers: number
    completedUsers: number
    inProgressUsers: number
    notStartedUsers: number
    avgCompletionPct: number
    medianCompletionPct: number
    modulesTotal: number
    modulesCompleted: number
    modulesInProgress: number
    modulesNotStarted: number
  }
  points?: Array<{
    moduleId: string
    moduleName?: string | null
    completionPct: number
    completedUsers: number
    inProgressUsers: number
    notStartedUsers: number
  }>
  users: Array<{
    userId: string
    alias: string | null
    name?: string | null
    email?: string | null
    jobTitle?: string | null
    department?: string | null
    progressPct: number
    eta: string | null
    modulesCompleted?: number
    modulesInProgress?: number
    modulesTotal?: number
    status?: "completed" | "inProgress" | "notStarted"
    lastActivity?: string | null
  }>
}

export async function getTrainingStatus(force = false): Promise<AdminTrainingStatusResponse> {
  const url = force ? `/admin/training/status?force=true` : `/admin/training/status`
  const response = await api.get(url)
  return response.data || response
}

// Training catalog (static + Cosmos overrides)
export type AdminTrainingSectionRow = {
  id: string
  display_name: string
  url: string
  component: string
  catalog_enabled: boolean
  enabled: boolean
  access: AccessPolicy
  access_configured: boolean
  access_overridden: boolean
}

export type AdminTrainingModuleRow = {
  id: string
  display_name: string
  catalog_enabled: boolean
  enabled: boolean
  access: AccessPolicy
  access_configured: boolean
  sections: AdminTrainingSectionRow[]
  track_ids: string[]
}

export type AdminTrainingTrack = {
  id: string
  display_name: string
  order: number
  module_ids: string[]
  quiz_section_id: string | null
}

export type AdminTrainingCatalogResponse = {
  modules: AdminTrainingModuleRow[]
  tracks: AdminTrainingTrack[]
  base_access: AccessPolicy
  base_access_configured: boolean
}

export async function getTrainingCatalog(): Promise<AdminTrainingCatalogResponse> {
  const response = await api.get("/admin/training/catalog")
  const data = response.data || response
  return data as AdminTrainingCatalogResponse
}

export async function updateTrainingModuleStatus(
  moduleId: string,
  enabled: boolean
): Promise<{ success: boolean }> {
  const response = await api.put(`/admin/training/modules/${moduleId}/status`, { enabled })
  const data = response.data || response
  if (data.success === false) {
    throw new Error(data.message || "Failed to update training module status")
  }
  return data
}

export async function updateTrainingSectionStatus(
  moduleId: string,
  sectionId: string,
  enabled: boolean
): Promise<{ success: boolean }> {
  const response = await api.put(
    `/admin/training/modules/${moduleId}/sections/${sectionId}/status`,
    { enabled }
  )
  const data = response.data || response
  if (data.success === false) {
    throw new Error(data.message || "Failed to update training section status")
  }
  return data
}

export async function getTrainingBaseAccessPolicy(): Promise<AccessPolicy> {
  const response = await api.get("/admin/training/base/access")
  const data = response.data || response
  return data.access || {}
}

export async function updateTrainingBaseAccessPolicy(
  policy: AccessPolicyUpdate
): Promise<AccessPolicy> {
  const response = await api.put("/admin/training/base/access", policy)
  const data = response.data || response
  return data.access || {}
}

export async function getTrainingModuleAccessPolicy(moduleId: string): Promise<AccessPolicy> {
  const response = await api.get(`/admin/training/modules/${moduleId}/access`)
  const data = response.data || response
  return data.access || {}
}

export async function updateTrainingModuleAccessPolicy(
  moduleId: string,
  policy: AccessPolicyUpdate
): Promise<AccessPolicy> {
  const response = await api.put(`/admin/training/modules/${moduleId}/access`, policy)
  const data = response.data || response
  return data.access || {}
}

export async function clearTrainingModuleAccessPolicy(moduleId: string): Promise<void> {
  await api.delete(`/admin/training/modules/${moduleId}/access`)
}

export async function getTrainingSectionAccessPolicy(
  moduleId: string,
  sectionId: string
): Promise<AccessPolicy> {
  const response = await api.get(`/admin/training/modules/${moduleId}/sections/${sectionId}/access`)
  const data = response.data || response
  return data.access || {}
}

export async function updateTrainingSectionAccessPolicy(
  moduleId: string,
  sectionId: string,
  policy: AccessPolicyUpdate
): Promise<AccessPolicy> {
  const response = await api.put(
    `/admin/training/modules/${moduleId}/sections/${sectionId}/access`,
    policy
  )
  const data = response.data || response
  return data.access || {}
}

export async function clearTrainingSectionAccessPolicy(
  moduleId: string,
  sectionId: string
): Promise<void> {
  await api.delete(`/admin/training/modules/${moduleId}/sections/${sectionId}/access`)
}

export type TrainingGatingConfig = {
  required_module_ids: string[]
}

export async function getTrainingGatingConfig(): Promise<TrainingGatingConfig> {
  const response = await api.get("/admin/training/gating")
  const data = response.data || response
  return data as TrainingGatingConfig
}

export async function updateTrainingGatingConfig(
  config: TrainingGatingConfig
): Promise<TrainingGatingConfig> {
  const response = await api.put("/admin/training/gating", config)
  const data = response.data || response
  if (data.success === false) {
    throw new Error(data.message || "Failed to update training gating config")
  }
  return data as TrainingGatingConfig
}

export async function getTrainingGatingRaw(): Promise<{
  entity: { required_module_ids: string[] }
  hash: string
}> {
  const response = await api.get("/admin/training/gating/raw")
  return response.data || response
}

export async function saveTrainingGatingRaw(args: {
  entity: { required_module_ids: string[] }
  expectedHash: string
  reason: string
}): Promise<{ success: boolean; entity: { required_module_ids: string[] }; hash: string }> {
  const { entity, expectedHash, reason } = args
  const response = await api.put("/admin/training/gating/raw", {
    entity,
    expected_hash: expectedHash,
    reason,
    source: "raw",
  })
  return response.data || response
}
